import React, { useState, useEffect } from 'react';
import {
  IdentificationIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  DocumentTextIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { API_BASE as API } from '../../lib/config';

const IdentityVerificationManagementNew = ({ onBack }) => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processingVerificationId, setProcessingVerificationId] = useState(null);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      console.log('Fetching verifications with token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(`${API}/admin/identity-verifications?status=${selectedTab}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('API Response:', data);
        const verificationsList = Array.isArray(data) ? data : (data.verifications || []);
        console.log('Verifications List:', verificationsList);
        setVerifications(verificationsList);
      } else {
        console.error('API Error:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('Error details:', errorData);
        setVerifications([]);
        toast.error('本人確認一覧の取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching verifications:', error);
      setVerifications([]);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerifications();
  }, [selectedTab]);

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  // Document type translation
  const getDocumentTypeName = (type) => {
    const typeMap = {
      'passport': 'パスポート',
      'drivers_license': '運転免許証',
      'mynumber_card': 'マイナンバーカード',
      'resident_card': '住民基本台帳カード'
    };
    return typeMap[type] || type;
  };

  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case 'in_progress': return 'text-yellow-600 bg-yellow-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in_progress': return '審査中';
      case 'completed': return '承認済';
      case 'rejected': return '却下';
      default: return '不明';
    }
  };

  const handleViewDetail = (verification) => {
    setSelectedVerification(verification);
    setShowDetail(true);
  };

  const handleApprove = async (verificationId) => {
    setProcessingVerificationId(verificationId);
    
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const response = await fetch(`${API}/admin/identity-verifications/${verificationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'completed',
          admin_comment: '本人確認書類が承認されました'
        })
      });

      if (response.ok) {
        toast.success('本人確認が承認されました');
        setShowDetail(false);
        await fetchVerifications();
      } else {
        toast.error('承認処理に失敗しました');
      }
    } catch (error) {
      console.error('Error approving verification:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setProcessingVerificationId(null);
    }
  };

  const handleReject = async (verificationId, reason) => {
    setProcessingVerificationId(verificationId);
    
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const response = await fetch(`${API}/admin/identity-verifications/${verificationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'rejected',
          admin_comment: reason
        })
      });

      if (response.ok) {
        toast.success('本人確認を却下しました');
        setShowDetail(false);
        setShowRejectModal(false);
        setRejectReason('');
        await fetchVerifications();
      } else {
        toast.error('却下処理に失敗しました');
      }
    } catch (error) {
      console.error('Error rejecting verification:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setProcessingVerificationId(null);
    }
  };

  // Safe filtering with React.useMemo
  const filteredVerifications = React.useMemo(() => {
    if (!Array.isArray(verifications)) {
      return [];
    }
    
    return verifications.filter(v => {
      if (selectedTab === 'all') return true;
      if (selectedTab === 'pending') return v.status === 'in_progress';
      if (selectedTab === 'approved') return v.status === 'completed';
      if (selectedTab === 'rejected') return v.status === 'rejected';
      return true;
    });
  }, [verifications, selectedTab]);

  if (showDetail && selectedVerification) {
    return (
      <div className="space-y-6">
        {/* 戻るボタン */}
        <div className="flex items-center">
          <button
            onClick={() => setShowDetail(false)}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            戻る
          </button>
        </div>

        {/* 詳細表示 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">本人確認書類詳細</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ユーザー情報 */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">申請者情報</h4>
                  <div className="space-y-2">
                    <p><span className="text-gray-600">氏名:</span> {selectedVerification.user_name}</p>
                    <p><span className="text-gray-600">メールアドレス:</span> {selectedVerification.user_email}</p>
                    <p><span className="text-gray-600">書類種類:</span> {getDocumentTypeName(selectedVerification.document_type)}</p>
                    <p><span className="text-gray-600">提出日時:</span> {formatDateTime(selectedVerification.submitted_at)}</p>
                    <div>
                      <span className="text-gray-600">ステータス:</span>
                      <span className={`ml-2 px-2 py-1 rounded text-sm font-medium ${getStatusColor(selectedVerification.status)}`}>
                        {getStatusText(selectedVerification.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedVerification.admin_comment && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">管理者コメント</h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedVerification.admin_comment}</p>
                  </div>
                )}
              </div>

              {/* 書類画像 */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">提出書類（表面）</h4>
                  {selectedVerification.document_front_image ? (
                    <img
                      src={selectedVerification.document_front_image}
                      alt="本人確認書類（表面）"
                      className="w-full rounded-lg border border-gray-200"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-500">画像が登録されていません</p>
                    </div>
                  )}
                </div>

                {selectedVerification.document_back_image && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">提出書類（裏面）</h4>
                    <img
                      src={selectedVerification.document_back_image}
                      alt="本人確認書類（裏面）"
                      className="w-full rounded-lg border border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 承認・却下ボタン */}
            {selectedVerification.status === 'in_progress' && (
              <div className="mt-6 flex gap-4">
                <button
                  onClick={() => handleApprove(selectedVerification.id)}
                  disabled={processingVerificationId === selectedVerification.id}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {processingVerificationId === selectedVerification.id ? '処理中...' : '承認'}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={processingVerificationId === selectedVerification.id}
                  className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  却下
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 却下理由モーダル */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">却下理由の入力</h3>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="却下理由を入力してください"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
              />
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleReject(selectedVerification.id, rejectReason)}
                  disabled={!rejectReason.trim() || processingVerificationId === selectedVerification.id}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  却下する
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      <div className="flex items-center">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          戻る
        </button>
      </div>

      {/* ヘッダー */}
      <div className="flex items-center">
        <IdentificationIcon className="w-8 h-8 text-blue-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">本人確認管理</h1>
      </div>

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'all', label: 'すべて' },
            { key: 'pending', label: '審査中' },
            { key: 'approved', label: '承認済' },
            { key: 'rejected', label: '却下' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* コンテンツ */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">読み込み中...</p>
        </div>
      ) : filteredVerifications.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    申請者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    書類種類
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    提出日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVerifications.map((verification) => (
                  <tr key={verification.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {verification.user_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {verification.user_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getDocumentTypeName(verification.document_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(verification.submitted_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(verification.status)}`}>
                        {getStatusText(verification.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDetail(verification)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        詳細
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">該当する本人確認申請がありません</p>
        </div>
      )}
    </div>
  );
};

export default IdentityVerificationManagementNew;